import type { CommonOptionsType } from '@/definition/common/data.d';
import type { ReactNode } from 'react';
import type {
  ButtonProps,
  CascaderProps,
  InputProps,
  ListProps,
  PopupProps,
  TagInputProps,
  SelectProps as TdSelectProps,
  TooltipProps,
} from 'tdesign-react';

// 下拉框列表最大高度
export const listMaxHeight456 = 456;
export const listMaxHeight368 = 368;

/** 输入框 大小 - 枚举 */
export enum InputSizeEnum {
  S = 's',
  M = 'm',
  L = 'l',
  XL = 'xl',
  FULL = 'full',
}
/** 输入框 大小 - 类型 */
export type InputSizeType = 's' | 'm' | 'l' | 'xl' | 'full' | string;

/** 下拉选择 - 列表 */
export type SelectItemType = {
  label: string;
  value: any;
  text?: any;
  disabled?: boolean;
  // 分组表示
  groupKey?: string;
  // 选中全部数据
  selectedItem?: any;
};

/** 下拉选择 - 展示文本类型 */
export type DisplayTextType =
  | 'tag'
  | 'text'
  | 'displayText'
  | 'customText'
  | 'autoComplete';

/** 下拉选择 - 展示文本类型 */
export enum DisplayTextTypeEnum {
  /** 默认文本模式 */
  TEXT = 'text',
  /** 标签模式 */
  TAG = 'tag',
  /** 使用displayText展示内容 */
  DISPLAY_TEXT = 'displayText',
  /** 自定义 */
  CUSTOM_TEXT = 'customText',
  /** 自动完成 */
  AUTO_COMPLETE = 'autoComplete',
}

/** 下拉选择 - 部分开放popup参数 */
export type SelectPopupProps = {
  trigger?: PopupProps['trigger'];
  placement?: PopupProps['placement'];
  width?: any;
  maxColumnWidth?: DropdownProps['maxColumnWidth'];
  minColumnWidth?: DropdownProps['minColumnWidth'];
  overlayClassName?: PopupProps['overlayClassName'];
  overlayInnerStyle?: PopupProps['overlayInnerStyle'];
  overlayStyle?: PopupProps['overlayStyle'];
};

/** 基础版 - 下拉参数 */
export type SelectBseProps = {
  className?: string;
  /** 底部 */
  footer?: ReactNode;
  value?: any;
  /** 占位提示语 */
  placeholder?: string;
  /** 输入框大小 */
  size?: InputSizeType;
  /** 多选 */
  multiple?: boolean;
  /** 搜索 */
  searchable?: boolean;
  /** 禁用 */
  disabled?: boolean;
  /** 只读 */
  readonly?: boolean;
  /** 加载中 */
  loading?: boolean;
  /** 加载中的文本 */
  loadingText?: string;
  /** 阴影层 */
  shadow?: boolean;
  /** 阴影层 - 是否可关闭下拉 */
  shadowIsClose?: boolean;
  /** 清除 */
  clearable?: boolean;
  /** 下拉文本自适应宽度 默认 false */
  autoWidth?: boolean;
  /** 下拉列表与下拉按钮同宽 默认 true */
  matchButtonWidth?: boolean;
  /** 是否隐藏边框 默认 false*/
  borderless?: boolean;
  /** 是否阻止点击冒泡 默认 false */
  isStopPropagation?: boolean;
  /** 是否打开展开收起 */
  isExpanded?: boolean;
  /** 默认展开收起 - 默认 false */
  defaultExpanded?: boolean;
  /** 鼠标悬浮文本 */
  suspendedText?: string;
  /** 展示文本 */
  displayText?: string;
  displayOptions?: any;
  /** 展示文本 - key */
  displayTextKey?: string;
  /** 展示文本类型 */
  displayTextType?: DisplayTextType;
  /** 是否开启 display-text 的样式 */
  isDisplayTextStyle?: boolean;
  /** 前置图标 */
  prefixIcon?: InputProps['prefixIcon'];
  prefixIconName?: string;
  /** 后置图标 */
  suffixIcon?: InputProps['suffixIcon'];
  suffixIconName?: string;
  /** 暂无数据文案 */
  emptyText?: OptionListProps['emptyText'];
  // tag - input - props
  minCollapsedNum?: TagInputProps['minCollapsedNum'];
  collapsedItems?: TagInputProps['collapsedItems'];
  valueDisplay?: TagInputProps['valueDisplay'];
  /** 开启collapsedTooltipProps，默认的提示组件参数 */
  collapsedToolTipProps?: TooltipProps;
  /** 清除时触发  */
  onClearable?: () => void;
  onClick?: () => void;
  onChange?: (val: any, item?: CommonOptionsType) => void;
  /** 监听是否打开 */
  onVisibleChange?: PopupProps['onVisibleChange'];
};

/** 下拉选择 - 列表 - 选项 */
export type OptionListProps = {
  /** 是否开启搜索框 */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** 是否开启 全部选项 */
  allOption?: boolean;
  /** 是否多选 -  默认false */
  multiple?: boolean;
  /** 是否溢出隐藏 -  默认true */
  ellipsis?: boolean;
  /** 搜索框自动聚焦 */
  autofocus?: boolean;
  options: CommonOptionsType[];
  value?: any;
  /** 标题 key  默认：label */
  displayTextKey?: string;
  /** 暂无数据文案 */
  emptyText?: string | ReactNode;
  /** 列表 - 头部 */
  listHeader?: string | ReactNode;
  /** 列表最大高度 */
  listMaxHeight?: number;
  /** 列表滚动时触发，scrollTop 表示顶部滚动距离，scrollBottom 表示底部滚动距离 */
  onScroll?: ListProps['onScroll'];
  /** 自定义渲染选项 */
  customOption?: (
    item: CommonOptionsType & { option: any },
  ) => ReactNode | string;
  /** 关闭时触发  */
  onClose?: () => void;
  /** 清除时触发  */
  onClearable?: () => void;
  /** 点击选项时触发  */
  onClick?: (val: any, item: CommonOptionsType) => void;
  /**
   * 值变化时触发，context.current 表示当前变化的数据值，如果是全选则为空；
   * context.type 表示引起选中数据变化的是选中或是取消选中；
   * context.option 表示当前变化的数据项
   */
  onChange?: (val: any, item: CommonOptionsType) => void;
};

/** 下拉选择 - 分组列表 - 配置标题 */
interface groupTitleItemType {
  title: string;
  sort?: number;
  render?: () => void;
}

/** 下拉选择 - 分组列表 - 配置 */
export type GroupListProps = {
  value?: any;
  groupTitleJson: Record<any, groupTitleItemType>;
  groupKey: string;
  /** 以什么方式排序 */
  groupSortType?: 'title' | 'list' | 'sort';
  options: CommonOptionsType[];
  searchPlaceholder?: string;
  size?: InputSizeType;
  searchable?: boolean;
  multiple?: boolean;
  /** 搜索框自动聚焦 */
  autofocus?: boolean;
  /** 是否打开展开收起 */
  isExpanded?: boolean;
  /** 默认展开收起 - 默认 false */
  defaultExpanded?: boolean;
  displayTextKey?: string;
  /** 暂无数据文案 */
  emptyText?: string | ReactNode;
  /** 列表 - 头部 */
  listHeader?: string | ReactNode;
  /** 分组分割线 */
  groupDividingLine?: boolean;
  customOption?: OptionListProps['customOption'];
  onChange?: OptionListProps['onChange'];
  onClick?: OptionListProps['onClick'];
};

/** 下拉选择 - 分页模式 */
export type PagingSelectProps = {
  className?: string;
  value?: any;
  placeholder?: string;
  /** 是否多选 */
  multiple?: boolean;
  /** 是否禁用 */
  disabled?: boolean;
  /** 是否开启清除 */
  clearable?: boolean;
  /** 大小 */
  size?: InputSizeType;
  /** 自动宽度 */
  autoWidth?: boolean;
  /** 下拉列表与下拉按钮同宽 默认 true */
  matchButtonWidth?: boolean;
  /** 无边框模式 */
  borderless?: boolean;
  /** 自动聚焦 */
  autofocus?: boolean;
  /** 鼠标悬浮文本 */
  suspendedText?: string;
  /** 展示文本 - 单选时 */
  displayText?: string;
  /** 展示文本 - 多选时 */
  displayOptions?: TdSelectProps['options'];
  /** 展示文本 - 类型 */
  displayTextType?: DisplayTextType;
  /** 自定义无数据提示文案 */
  emptyText?: string | ReactNode;
  /** 前置图标 */
  prefixIcon?: InputProps['prefixIcon'];
  prefixIconName?: string;
  /** 后置图标 */
  suffixIcon?: InputProps['suffixIcon'];
  suffixIconName?: string;
  // tag - input - props
  minCollapsedNum?: TagInputProps['minCollapsedNum'];
  collapsedItems?: TagInputProps['collapsedItems'];
  valueDisplay?: TagInputProps['valueDisplay'];
  /** 开启collapsedTooltipProps，默认的提示组件参数 */
  collapsedToolTipProps?: TooltipProps;
  /** 自定义渲染 */
  customOption?: OptionListProps['customOption'];
  /** 关闭回调 */
  onClose?: () => void;
  /** 清除回调 */
  onClearable?: () => void;
  /** 监听值 */
  onChange?: (val: any, config: any) => void;
  /** 监听是否打开 */
  onVisibleChange?: (val: boolean) => void;
};

/** 分页列表 */
export type ScrollListProps = {
  /** 是否展示搜索 */
  searchable?: boolean;
  /** data - 层级的key 默认空 */
  dataKey?: string;
  /** list - 层级的key 默认dataList */
  listKey?: string;
  /** 标题搜索key */
  searchKey?: string;
  /** 文本渲染key */
  displayTextKey?: string;
  /** 是否开启默认请求 - 默认false */
  isAutoRequest?: boolean;
  /** 是否重置刷新列表 - 默认false */
  isResetSearch?: boolean;
  /** 是否多选 -  默认false */
  multiple?: boolean;
  /** 是否溢出隐藏 -  默认true */
  ellipsis?: boolean;
  /** 搜索框是否自动聚焦 -  默认true */
  autofocus?: boolean;
  /** 暂无数据 */
  emptyText?: string | ReactNode;
  /** 搜索占位提示语 */
  searchPlaceholder?: string;
  /** 列表 - 头部 */
  listHeader?: string | ReactNode;
  /** 底部内容 */
  footer?: ReactNode;
  /** 默认下拉窗是否打开 */
  defaultVisible?: boolean;
  /** 默认搜索参数 */
  defaultSearchParams?: Record<string, any>;
  /** 默认列表 */
  defaultDataList?: any[];
  /** 值 */
  value?: any;
  /** 过滤列表方法，使之结构可用于组件 */
  getFilterOptions?: (list: any[], pageInfo: PageInfoIF) => any[];
  /** 获取列表接口 */
  getList?: (data: any) => Promise<any>;
  /** 过滤列表 */
  onFilter?: (data: any) => any;
  /** 监听值变化  */
  onChange?: TdSelectProps['onChange'];
  /** 关闭 */
  onClose?: () => void;
  /** 请求成功 */
  onReqSuccess?: (data: any[]) => void;
  /** 请求结果为空 */
  onReqEmpty?: (data: any, searchParams: Record<string, any>) => void;
  /** 设置重置刷新列表 */
  setIsResetSearch?: (val: boolean) => void;
};

/** 下拉选择 - 参数 */
export type SelectProps = {
  /** 是否分组 */
  isGroup?: boolean;
  groupKey?: string;
  /** 分组-排序类型 */
  groupSortType?: GroupListProps['groupSortType'];
  /** 分组标题对象 */
  groupTitleJson?: any;
  /** 分组分割线 */
  groupDividingLine?: GroupListProps['groupDividingLine'];
  /** popup参数 */
  popupProps?: PopupProps;
} & SelectBseProps &
  OptionListProps &
  SelectPopupProps;

/** 图标组件 */
export type IconProps = {
  mode?: 'font' | 'svg';
  className?: string;
  style?: CSSProperties;
  name?: string;
  size?: string | number;
  fontSize?: string | number;
  color?: string;
  bold?: boolean;
  onClick?: () => void;
};

/** 缺省页 - 状态参数 */
export enum DefaultPageStatusEnum {
  /** 加载中 */
  LOADING = 'loading',
  /** 无数据 - 默认 */
  NO_DATA = 'noData',
  /** 无数据 - 搜索/筛选 */
  SEARCH_NO_DATA = 'searchNoData',
  /** 无数据 - 报表 */
  NO_DATA_REPORT = 'noDataReport',
  /** 无权限 */
  NO_AUTH = 'noAuth',
  /** 网络连接失败 */
  NO_NETWORK = 'noNetwork',
  /** 自定义 */
  CUSTOM = 'custom',
}

/** 按钮 */
export type ButtonPropsType = {
  className?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  type?: ButtonProps['type'];
  style?: ButtonProps['style'];
  /** 使用用途： 主、次、取消 */
  useType?: 'main' | 'sub' | 'cancel';
  /** 按钮主题 依次为默认色、品牌色、危险色、警告色、成功色 */
  theme?: ButtonProps['theme'];
  /** 按钮形式 基础、线框、虚线、文字 */
  variant?: ButtonProps['variant'];
  /**
   * rectangle - 长方形
   * square - 正方形
   * round - 圆角长方形
   * circle - 圆形
   **/
  shape?: ButtonProps['shape'];
  /** 按钮 - 开发者自定义大小 */
  size?: InputSizeType;
  /** 前置图标 */
  prefixIcon?: ReactNode;
  prefixIconName?: string;
  /** 后置图标 */
  suffixIcon?: ReactNode;
  suffixIconName?: string;
  /** 提示语 */
  tooltip?: ReactNode | string;
  /** 浮层出现位置 */
  tooltipPlacement?: TooltipProps['placement'];
  tooltipOffset?: any[];
  buttonProps?: ButtonProps;
  tipProps?: TooltipProps;
  overlayInnerClassName?: TooltipProps['overlayInnerClassName'];
  onClick?: ButtonProps['onClick'];
};

/** 级联选择下拉选择 - 列表 - 选项 */
export type CascaderOptionListProps = {
  /** 类名 */
  className?: string;
  /** 是否开启搜索框 */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** 是否开启 全部选项 */
  allOption?: boolean;
  /** 是否多选 -  默认false */
  multiple?: boolean;
  /** 是否溢出隐藏 -  默认true */
  ellipsis?: boolean;
  options: CascaderCommonOptionsType[];
  value?: any;
  /** 标题 key  默认：label */
  displayTextKey?: string;
  /** 暂无数据文案 */
  emptyText?: string | ReactNode;
  /** 是否分组 */
  isGroup?: boolean;
  /** 获取分组值的key */
  groupKey?: string;
  /** 列表最大高度 */
  listMaxHeight?: number;
  /** 列表 - 头部 */
  listHeader?: (params: {
    item: { parentId: number | string; leavel: number; list: any[] };
  }) => string | ReactNode | null;
  /** 列表滚动时触发，scrollTop 表示顶部滚动距离，scrollBottom 表示底部滚动距离 */
  onScroll?: ListProps['onScroll'];
  /** 自定义渲染选项 */
  customOption?: (
    item: CascaderCommonOptionsType & { option: any },
  ) => ReactNode | string;
  /** 关闭时触发  */
  onClose?: () => void;
  /** 清除时触发  */
  onClearable?: () => void;
  /** 点击选项时触发  */
  onClick?: (val: any, item: CascaderCommonOptionsType) => void;
  /**
   * 值变化时触发，context.current 表示当前变化的数据值，如果是全选则为空；
   * context.type 表示引起选中数据变化的是选中或是取消选中；
   * context.option 表示当前变化的数据项
   */
  onChange?: (val: any, item: CascaderCommonOptionsType) => void;
} & Omit<CascaderProps, 'valueDisplay' | 'size'>;

// 级联选择器参数
export type CascaderSelectProps = {
  /** popup参数 */
  popupProps?: PopupProps;
  /** 完整路径名称 */
  fullPathName?: boolean;
  /** 选中结果框渲染key */
  displaySelectedResTextKey?: string;
} & SelectBseProps &
  CascaderOptionListProps &
  SelectPopupProps;
